import type {AiGuideFeedbackProvidedV6} from "@khanacademy/event-schemas";
import type {KAError} from "@khan/logging";

export type AssignRecommendationsGroup = null | {
    classroomDescriptor: string;
    studentKaids: string[];
    contentDescriptors: string[];
    groupName: string;
};

export type ThreadID = string;
export type InteractionID = string;

export enum FeedbackType {
    General = "General",
    Positive = "Positive",
    Negative = "Negative",
    Quick = "Quick",
}

export type CooldownType = "AntiCheat" | "TeacherDirected" | "None";

export interface CooldownStatus {
    hasCooldown: boolean;
    cooldownType: CooldownType;
    teacherName: null | string;
    teacherKaid: null | string;
    expiresAt: null | Date;
}

export type HideChatOptions =
    | {type: "ActivityWithoutChat"; message: null}
    | {type: "Default"; message: string | null}
    | {type: "Cooldown"; message: string}
    | {type: "InterestsOff"; message: string}
    | {type: "FocusMode"; message: string};
type Sentiment = "thumbs-up" | "thumbs-down";
type PositiveIssue =
    | "good-math"
    | "useful"
    | "compassionate"
    | "rigorous"
    | "growth-mindset"
    | "other";
type NegativeIssue =
    | "bad-math"
    | "inaccurate"
    | "harmful"
    | "not-useful"
    | "other";

/**
 * A merged state for feedback.
 *
 * We allow users to give both WrittenFeedback and QuickFeedback on a single
 * interaction, so we need to combine those into one thing. Because prior feedback
 * is merged with existing feedback in merge-feedback.ts, we allow for
 * now-deprecated property, reaction, on MergedFeedback type
 *
 * TODO(marnikostman): remove reaction from type if safe
 */
export interface MergedFeedback {
    type: FeedbackType;
    positive: boolean;
    reaction?: PositiveIssue | NegativeIssue;
    rating?: number;
    note?: string | null;
    rewrittenResponse?: string | null;
    sentiment?: EmojiSentiment;
}

// Feedback that can contain a written component and a numerical rating.
interface GeneralFeedback extends MergedFeedback {
    type: FeedbackType.General;
    positive: true;
    reaction?: "other";
    rating: number;
    note: string | null | undefined;
}

// Negative feedback that can contain a specific issue type, and possible
// further written feedback.
interface NegativeFeedback extends MergedFeedback {
    type: FeedbackType.Negative;
    positive: false;
    reaction?: NegativeIssue;
    note: string | null | undefined;
    rewrittenResponse: string | null | undefined;
    issue: AiGuideFeedbackProvidedV6["feedbackDropDown"];
}

// Positive feedback that can contain a specific praise type, and possible
// further written feedback.
interface PositiveFeedback extends MergedFeedback {
    type: FeedbackType.Positive;
    positive: true;
    reaction?: PositiveIssue;
    note: string | null | undefined;
    rewrittenResponse: string | null | undefined;
}

// Only these types have free-form text feedback fields.
export type WrittenFeedback =
    | GeneralFeedback
    | NegativeFeedback
    | PositiveFeedback;

// Feedback given by thumb up/thumbs down emoji, with no written component.
interface QuickFeedback extends MergedFeedback {
    type: FeedbackType.Quick;
    positive: boolean;
    sentiment: EmojiSentiment;
}

// Users can give feedback on individual interactions.
export type InteractionFeedback = WrittenFeedback | QuickFeedback;
export type FeedbackDropDown = AiGuideFeedbackProvidedV6["feedbackDropDown"];
// Reactions and issue types are now synonymous.
export type Reaction = PositiveIssue | NegativeIssue | EmojiSentiment;
// A user's quick, emoji based reaction to an interaction.
export type EmojiSentiment = Sentiment;

/**
 * ActionBubble represents a pre-baked prompt that the AIGuide provides
 * the user in its response. Used to display ActionBubbles.
 */
export interface ActionBubble {
    // Label is the text that is displayed to the user in the action bubble
    label: string;

    // Command is the text that is sent into the chat when the user clicks
    // on the action bubble.
    command: string;

    // StoredLabel is text that is saved in the `actionBubble` field of
    // the `AiGuideInteraction` record instead of the Label when a user clicks
    // on the action bubble. If it is empty, then the Label is used instead.
    storedLabel?: string;

    // Actions contains information about events that are triggered when the
    // user clicks on the action bubble. Actions are defined by the backend, but
    // triggered and handled by the client.
    actions?: ReadonlyArray<Action>;
    variant?: "primary" | "secondary" | "tertiary";

    // Dictates how the action bubble should behave when attached to an older
    // message in the chat window (not the latest message). By default, if
    // unset, the action bubbles will remain visible and clickable.
    oldMessageBehavior?: "disable" | "hide";

    // If true, isDynamic indicates that the action bubble was generated
    // dynamically by the bot rather than statically defined.
    isDynamic?: boolean;
}

export interface Action {
    actionType: ActionType;
    parameters: ActionTypeParameters;
}

export interface ActionPromiseCallbacks {
    onActionResolved: (message: string) => void;
    onActionRejected: (message: string) => void;
}

// sync-start:actionTypes 1866916403 https://github.com/Khan/webapp/blob/master/services/ai-guide/models/client_conversation_entry.go
export enum ActionType {
    navigation = "navigation",
    setPromptVariable = "setPromptVariable",
    openModal = "openModal",
    coeditorInsertText = "coeditorInsertText",
    newChat = "newChat",
    copyToClipboard = "copyToClipboard",
    replaceHighlightedText = "replaceHighlightedText",
    newPrefilledChat = "newPrefilledChat",
}

export const ActionTypes = Object.freeze(Object.values(ActionType));
export type ActionTypeParameters =
    | ActionTypeParametersModal
    | ActionTypeParametersSetPromptVariable
    | ActionTypeParametersCoeditorInsertText
    | ActionTypeParametersNavigation
    | ActionTypeParametersNewChat
    | ActionTypeParametersNewPrefilledChat;

export interface ActionTypeParametersNavigation {
    targetPath: string;
}

export interface ActionTypeParametersSetPromptVariable {
    promptVariable: string;
    value: string;
}

export interface ActionTypeParametersCoeditorInsertText {
    text: string;
}

export type ActionTypeParametersModal =
    ActionTypeParametersModalAssignRecommendationsGroup;
export type ActionTypeParametersNewChat = Empty;

export interface ActionTypeParametersNewPrefilledChat {
    prefilledInput: string;
}

/**
 * ActionTypeParametersModal has sub types that switch off of the modalID.
 */
export interface ActionTypeParametersModalBase {
    modalID: string;
}
// sync-end:actionTypes

// sync-start:ActionTypeParametersModalAssignRecommendationsGroup 1303272459 https://github.com/Khan/webapp/blob/master/services/ai-guide/models/client_conversation_entry.go
export type ActionTypeParametersModalAssignRecommendationsGroup =
    ActionTypeParametersModalBase & {
        classroomDescriptor: string;
        contentDescriptors: string;
        studentKaids: string;
        groupName: string;
    };
// sync-end:ActionTypeParametersModalAssignRecommendationsGroup

// sync-start:khanmigo-requested-input 1056567944 https://github.com/Khan/webapp/blob/master/services/ai-guide/models/client_conversation_entry.go
/**
 * RequestedInput represents a piece of information that a prompt config
 * requested from the user before proceeding. It will get stored in a prompt
 * variable for future use in prompts.
 */
export interface RequestedInput {
    label: string;
    initialValue?: string;
    description: string;
    variableName: string;
    required?: boolean;
    inputKind:
        | "TEXT_BOX"
        | "TEXT_AREA"
        | "SINGLE_SELECT"
        | "MULTI_SELECT"
        | "GRID_SELECT"
        | "CONTENT_SEARCH"
        | "DYNAMIC_TEXT_FIELDS"
        | "";
    selectOptions?: ReadonlyArray<{
        label: string;
        value: string;
        description?: string;
        tooltip?: string;
        additionalInputs?: ReadonlyArray<RequestedInput>;
    }>;
    shouldReplaceName?: boolean;
    maxCharacters?: number;
    maxFields?: number;
}
// sync-end:khanmigo-requested-input

/**
 * RelatedLink represents metadata for a content link that the AIGuide provides
 * in its response is related to the current context.
 */
export interface RelatedLink {
    contentKind: string;
    title: string;
    description: string;
    url: string;
    score: number;
}

/**
 * Represents a single chunk of audio message.
 */
export interface MessageAudioChunk {
    /** A string representing the audio data in base64 format. */
    base64Audio: string;
    /** A string representing the transcript of the audio. */
    transcript: string;
}

/**
 * Audio representation of a message, which is an array of MessageAudioChunks.
 */
export type MessageAudio = ReadonlyArray<MessageAudioChunk>;

export enum ActionStatusStreamAction {
    DYNAMIC_ACTION_BUBBLES = "DYNAMIC_ACTION_BUBBLES",
    AUDIO = "AUDIO",
    TEXT = "TEXT",
}

export enum ActionStatusStreamStatus {
    LOADING = "LOADING",
    SUCCESS = "SUCCESS",
    ERROR = "ERROR",
}

/**
 * Streamed data indicating a status update for a given action.
 */
export interface ActionStatusStreamData {
    action: ActionStatusStreamAction;
    status: ActionStatusStreamStatus;
}

export type ActionStatusMap = Map<
    ActionStatusStreamAction,
    ActionStatusStreamStatus
>;
export type ReadonlyActionStatusMap = ReadonlyMap<
    ActionStatusStreamAction,
    ActionStatusStreamStatus
>;

/**
 * Types to support communication with tutor endpoint
 */
export interface Message {
    text: string;
    user: "Student" | "Teacher" | "System";
    type: "incoming" | "outgoing" | "system";
    serverOnly: boolean;
    uiOnly: boolean;
    interactionId: string;
    requestedInputs?: ReadonlyArray<RequestedInput>;
    actionBubbles?: ReadonlyArray<ActionBubble>;
    links?: ReadonlyArray<RelatedLink>;
    contentFlagged?: boolean;
    isError?: boolean;
    isWarning?: boolean;
    entryClassification?: ConversationEntryClassification;
    isGeneratingResponse?: boolean;
    actionStatusMap?: ReadonlyActionStatusMap;
    attachedImage?: string;
    userInputImage?: boolean;
}

// sync-start:conversationEntry 1714035921 https://github.com/Khan/webapp/blob/master/services/ai-guide/models/conversation_entry.go
export enum ConversationEntryType {
    UserMessage = "user-message",
    ResponseToUser = "response-to-user",
    Moderated = "moderated",
    ActionBubbles = "action-bubbles",
    Variable = "variable",
    SystemMessage = "system-message",
    Miscellaneous = "miscellaneous",
    ExpandedKnowledgeBase = "expanded-knowledge-base",
    Essay = "essay",
    Config = "config",
    Annotation = "annotation",
    ResponseStopped = "response-stopped",
    Feedback = "feedback",
    RelatedLinks = "related-links",
    Tmv2Problem = "tmv2-problem",
    ConversationEnded = "conversation-ended",
    ImageInput = "image-input",
    CoeditingDocumentCreated = "coediting-document-created",
    CoeditingEditRequest = "coediting-edit-request",
    CoeditingEditResponse = "coediting-edit-response",
    TutoringAction = "tutoring-action",
}
// sync-end:conversationEntry

type PrepopulatedTextType = "prepopulated-text-action-bubble";
type ResponseStoppedReasonType = "Response was stopped by user";

interface EssayFeedback {
    feedback: string;
    score?: number;
}

interface SelectionCriteria {
    criteria: string;
}

interface ModerationFlagDetails {
    reason: string;
    severity: string;
}

// Base interface that all conversation entries must implement
interface BaseConversationEntry {
    type: ConversationEntryType;
    id: string;
    traceId: string;
    createdAt: Date;
    relatedToEntryId: string;
}

export interface UserMessageEntry extends BaseConversationEntry {
    type: ConversationEntryType.UserMessage;
    text: string;
    prepopulatedTextType?: PrepopulatedTextType;
    imageInputs?: ImageInput[];

    // TODO(KORE-1162) Remove after we're fully committed to ImageInput
    imageUrls?: string[];
}

export interface ResponseToUserEntry extends BaseConversationEntry {
    type: ConversationEntryType.ResponseToUser;
    text: string;
}

export interface SystemMessageEntry extends BaseConversationEntry {
    type: ConversationEntryType.SystemMessage;
    text: string;
}

export interface ModeratedEntry extends BaseConversationEntry {
    type: ConversationEntryType.Moderated;
    flagDetails?: ModerationFlagDetails;
    englishTranslatedText: string;
}

export interface ActionBubblesEntry extends BaseConversationEntry {
    type: ConversationEntryType.ActionBubbles;
    actionBubbles: ActionBubble[];
}

export interface VariableEntry extends BaseConversationEntry {
    type: ConversationEntryType.Variable;
    name: string;
    value: string;
}

export interface MiscellaneousEntry extends BaseConversationEntry {
    type: ConversationEntryType.Miscellaneous;
    requestedInputs?: RequestedInput[];
    essayFeedbackList?: EssayFeedback[];
}

export interface ExpandedKnowledgeBaseEntry extends BaseConversationEntry {
    type: ConversationEntryType.ExpandedKnowledgeBase;
    expandedKnowledgeId: string;
    text: string;
}

export interface EssayEntry extends BaseConversationEntry {
    type: ConversationEntryType.Essay;
    text: string;
}

export interface ConfigEntry extends BaseConversationEntry {
    type: ConversationEntryType.Config;
    url: string;
    pageType: string;
    pageTitle: string;
    configName: string;
    selectionCriteria: SelectionCriteria;
}

export interface AnnotationEntry extends BaseConversationEntry {
    type: ConversationEntryType.Annotation;
    annotations: string[];
}

export interface ResponseStoppedEntry extends BaseConversationEntry {
    type: ConversationEntryType.ResponseStopped;
    reason: ResponseStoppedReasonType;
}

export interface FeedbackEntry extends BaseConversationEntry {
    type: ConversationEntryType.Feedback;
    sentiment: Sentiment;
    reaction: Reaction;
    reactionNote: string;
    reactionRating: number;
    rewrittenResponse: string;
}

export interface RelatedLinksEntry extends BaseConversationEntry {
    type: ConversationEntryType.RelatedLinks;
    relatedLinks: RelatedLink[];
}

export enum Tmv2ProblemMode {
    Tmv2ProblemModeInitial = "initial",
    Tmv2ProblemModeTutoringProcedural = "tutoring",
    Tmv2ProblemModeTutoringStem = "tutoring-stem",
    Tmv2ProblemModeTutoringHumanities = "tutoring-humanities",
    Tmv2ProblemModeInvalidProblem = "invalid-problem",
    Tmv2ProblemModeSolved = "solved",
    Tmv2ProblemModeError = "error",
}

export interface Tmv2ProblemEntry extends BaseConversationEntry {
    type: ConversationEntryType.Tmv2Problem;
    mode: Tmv2ProblemMode;
    errorMessage: string;
}

export interface ConversationEndedEntry extends BaseConversationEntry {
    type: ConversationEntryType.ConversationEnded;
    endedReason: string;
}

export interface ImageInputEntry extends BaseConversationEntry {
    type: ConversationEntryType.ImageInput;
    imageMetadataID: string;
}

export interface CoeditingDocumentCreatedEntry extends BaseConversationEntry {
    type: ConversationEntryType.CoeditingDocumentCreated;
    documentId: string;
}

export interface CoeditingEditRequestEntry extends BaseConversationEntry {
    type: ConversationEntryType.CoeditingEditRequest;
    instruction: string;
    path: string[];
}

export interface CoeditingEditResponseEntry extends BaseConversationEntry {
    type: ConversationEntryType.CoeditingEditResponse;
    oldData: any;
    newData: any;
}

/**
 * TutoringAction describes the type of action that's represented by a
 * TutoringActionEntry in a tutoring conversation.
 */
export enum TutoringAction {
    TutoringActionUserSubmittedAttempt = "user-submitted-attempt",
    TutoringActionUserViewedSolution = "user-viewed-solution",
}

/**
 * TutoringActionEntry represents an action that happened during a tutoring
 * conversation.
 */
export interface TutoringActionEntry extends BaseConversationEntry {
    type: ConversationEntryType.TutoringAction;
    action: TutoringAction;
    actionValue?: string;
}

export type ConversationEntry =
    | UserMessageEntry
    | ResponseToUserEntry
    | SystemMessageEntry
    | ModeratedEntry
    | ActionBubblesEntry
    | VariableEntry
    | MiscellaneousEntry
    | ExpandedKnowledgeBaseEntry
    | EssayEntry
    | ConfigEntry
    | AnnotationEntry
    | ResponseStoppedEntry
    | FeedbackEntry
    | RelatedLinksEntry
    | Tmv2ProblemEntry
    | ConversationEndedEntry
    | ImageInputEntry
    | CoeditingDocumentCreatedEntry
    | CoeditingEditRequestEntry
    | CoeditingEditResponseEntry
    | TutoringActionEntry;

// sync-start:assignableActivitiesClassifications 838653069 https://github.com/Khan/webapp/blob/master/services/ai-guide/chat/assignable_activities/assignable_activities.go
export enum ConversationEntryClassification {
    AssignmentContext = "ASSIGNMENT CONTEXT",
}
// sync-end:assignableActivitiesClassifications

export type Conversation = ReadonlyArray<Readonly<Message>>;
export type RatedInteractions = ReadonlyMap<
    InteractionID,
    Readonly<MergedFeedback>
>;
export type ConversationSnapshot = Readonly<{
    /* The conversation */
    conversation: Conversation;
    /* The rated interactions for the conversation */
    ratedInteractions: RatedInteractions;
    /* ThreadID of the conversation. */
    threadId: ThreadID;
    /* The coeditor text. */
    coeditorText: string | null | undefined;
    /* The path that the conversation occurred on. */
    path: string;
}>;

export interface CompletionData {
    thread_id: string | null | undefined;
    question: string | null | undefined;
    prompt: string;
    temperature: number;
    max_tokens: number;
    stop: string | null | undefined;
    url: string;
}

/**
 * Dynamic bot descriptor.
 *
 * TODO: Type this better
 */
export interface DynamicBot {
    name?: string;
    prompt?: string;
    modifiers?: {name: string}[];
    events?: EventConfig[];
}

export interface EventConfig {
    type: string;
    fixedResponses: Array<FixedResponse>;
}

export interface FixedResponse {
    message: string;
    requestedInputs: RequestedInput[];
    actionBubbles: ActionBubble[];
}

// Users can use the chat UI as themselves, or see what a student
// might see.
export enum UserPersona {
    Teacher = "teacher",
    Parent = "parent",
    Student = "student",
    DistrictAdmin = "district_admin",
}

/**
 * Additional data about the chat input interaction.
 *
 * This is used for analytics.
 */
export interface ChatInputMetadata {
    /**
     * True if the user used speech-to-text to draft their request
     */
    containsSpeechToTextContent: boolean;
    /**
     * This value represents the voice that is used for the text-to-speech
     * audio response, unless overridden by the server. It is NULL if
     * requestWithTextToSpeech is false.
     */
    guideVoiceDescriptor: string | null;
    /**
     * If set, then the chat input was pre-populated (via action bubble,
     * message notification, etc).
     */
    prepopulatedText: string | null;
    /**
     * Type of request UI element that the user is using to initiate the
     * request – either typing their own request or a prepopulated request e.g.
     * from an action bubble or notification.
     */
    requestUIType:
        | "ACTION_BUBBLE"
        | "COEDITOR_SUGGESTION"
        | "DYNAMIC_ACTION_BUBBLE"
        | "NOTIFICATION_ACTION_BUBBLE"
        | "NOTIFICATION_MESSAGE_CTA"
        | "UPLOAD_ONLY"
        | "USER_TYPED";
    /**
     * True if the text-to-speech feature is enabled when the request is
     * submitted.
     */
    requestWithTextToSpeech: boolean;
    /**
     * The time it takes a user to draft their query and submit it.
     */
    timeToSubmit: number;
}

export type ReadingStyle = "LOW" | "MEDIUM" | "HIGH";

/**
 * Navigation info used by the AIGuide.
 */
export interface GuideNavigationInfo {
    /* The session id. Set whenever the user navigates to a new page. */
    id: string;
    /* The path of the current page. Set whenever the user navigates to a new
       page. */
    path: string;

    /* The assessment item ID. Set when navigating between practice problems in
       an exercise. */
    assessmentItemId?: string;

    /* The resolved content path of the current page (if within the content
       library), otherwise an empty content path (all nulls). */
    contentPath?: any;
}

export type GuideState = Readonly<{
    /* The persona for the activity */
    activityPersona: Nullable<string>;
    /* Set whether the chat responses will be buffered or streamed. */
    bufferResponse: boolean;
    /* The ID of the essay session for essay feedback activity */
    essaySessionId: string | null | undefined;
    /* Indicates if the user has acknowledged the terms of use for the AI
       guide */
    hasAcknowledgedGuide: boolean;
    /* Identity of the current in progress interaction, which can be stopped. */
    stopId: InteractionID | null | undefined;
    /* The path to the content that the Guide should use for context. */
    contentURLPath: string | null | undefined;
    /* A list of links that relate to the current page type. */
    contextualLinks: ReadonlyArray<string>;
    /* The current conversation with the bot. */
    conversation: Conversation;
    /* The ConversationEntry log */
    conversationEntries: ReadonlyArray<ConversationEntry>;
    /* Server-only messages to be injected into the conversation. */
    annotations: ReadonlyArray<string>;
    /* Provide the ability to programmatically define a new bot in code */
    dynamicBot: Readonly<DynamicBot> | null | undefined;
    /* Whether the bot has been initialized and has sent the opening
       prompt to the user. */
    initialized: boolean;
    /** A promise that resolves when the guide has been initialized. This is
     * null if there is no initialization in progress. */
    initializationPromise: Promise<void> | null;
    /* Indicates whether the guide is currently experiencing high traffic */
    isHighTraffic: boolean;
    /* Is the guide loading? */
    isLoading: boolean;
    /* Indicates whether the guide is not available to chat */
    isOffline: boolean;
    /**
     * This is used to distinguish between different offline states, such as
     * teacher-directed mode or anti-cheat mode.
     */
    cooldownStatus: CooldownStatus;
    /* Are the guide menus disabled? */
    menusDisabled: boolean;
    /* Overrides the model to be used. */
    modelOverride: string | null | undefined;
    /* The session ID and path set whenever the user navigates to a new page.
       An assessment item ID is set when navigating between practice problems
       in an exercise. */
    navigation: Readonly<GuideNavigationInfo>;
    /* Is the Guide UI open? */
    open: boolean;
    /* Support the features that allow users to submit reactions and feedback
       to individual interactions with the guide. */
    ratedInteractions: ReadonlyMap<InteractionID, Readonly<MergedFeedback>>;
    /* How much of the user's daily token quota remains; this is a string
       representation of a floating point number between 0 and 1 (inclusive)
       with 0 indicating that the user has used up all of their tokens for the
       day and 1 indicating that the user has not used any tokens for the day.
    */
    remainingQuota: number | null | undefined;
    /* How much time (seconds) until the next user message will be sent to the
    server */
    retryTimeoutSeconds: number;
    /* Provide the ability to programmatically select a bot by name */
    selectedBotName: string | null | undefined;
    /* The ID which maps to an asset used to change AI Guide's avatar appearance. */
    swagId: string;
    /* A list of images in the current conversation. */
    images: Array<ImageInput>;
    /* A teacher or parent requesting a student chat experience. */
    userAsStudent: boolean;
    /* If the user has used the "as student" persona toggle, we set this flag to true. */
    userHasToggledPersona: boolean;
    /* Overrides the temperature to be used. */
    temperatureOverride: number | null | undefined;
    /* The ID of the thread that is associated with the interactions. */
    threadId: ThreadID | null | undefined;
}>;

// sync-start:imageInputDTO 1139243332 https://github.com/Khan/webapp/blob/master/services/ai-guide/chat_types/chat_models.go
export enum ImageSource {
    ProvidedByFile = "ProvidedByFile",
    ProvidedByURL = "ProvidedByURL",
    DrawnInDoodlepad = "DrawnInDoodlepad",
    ProvidedByCamera = "ProvidedByCamera",
}

// An image input to Khanmigo.
export interface ImageInput {
    imageURL: string;
    interactionID?: string;
    moderated: boolean;
    source: ImageSource;
    isNewInput?: boolean;
}
// sync-end:imageInputDTO

export interface AIGuideMetadata {
    interactionId: InteractionID;
    threadId: ThreadID;
    conversation: Conversation;
    conversationEntries: ConversationEntry[];
    remainingQuota: string;
    feedbackMap?: RatedInteractions;
    restoredPromptVariables?: Record<string, string>;
    entryComponentOutput?: Record<string, string>;
}
export interface SendGuideItemOptions {
    assignmentID?: string;

    customPrompt?: string | null;

    promptVersionOverride?: string | null;

    demoStartDate?: string | null;

    demoEndDate?: string | null;

    /**
     * If set to true, the backend will generate audio data for the text
     * streamed back via the primary streaming callback.
     */
    generateVoice?: boolean;

    onResponseStart?: () => void;

    /**
     * Callback invoked when a complete response is received from the server.
     */
    onResponseComplete?: (
        metadata: AIGuideMetadata,
        answer: string,
        answerContainsAudio: boolean,
    ) => void;

    /**
     * Callback invoked when an error is received from the server.
     */
    onResponseFailed?: (error: KAError) => void;
}
// Voice is a string that identifies a voice on the backend. The string is
// called a "voice descriptor" because it identifies the text-to-speech
// provider along with the ID of the voice. The value is meant to be opaque
// to the frontend, but FYI the format looks "lmnt:khan-voice-1".
export type Voice = string;
export type Language = string;

export enum AIActivity {
    DefaultLearner = "activity-default-learner",
    DefaultTeacher = "activity-default-teacher",
    DefaultParent = "activity-default-parent",
    ChatWithHistoricalFigure = "activity-chat-with-historical-figure",
    ClassSnapshot = "activity-class-snapshot",
    AssignmentRecommendations = "activity-assignment-recommendations",
    ChatSummaryTeacher = "activity-get-chat-summary-for-teacher",
    ChatSummaryParent = "activity-get-chat-summary-for-parent",
    WriteClassNewsletter = "activity-write-class-newsletter",
    WriteRecommendationLetter = "activity-write-recommendation-letter",
    AcademicEssayFeedback = "activity-academic-essay-feedback",
    EssayFeedbackPitfalls = "activity-essay-feedback-pitfalls",
    PersonalizeInterests = "activity-personalize-interests",
    TutorMe = "activity-help-me-with-a-math-problem",
    TutorMeStem = "activity-tutor-me-stem",
}

// sync-start:khanmigo-client-notifications 1142423828 https://github.com/Khan/webapp/blob/master/services/ai-guide/client_notifications/handle_dispatch_request.go
export type NotificationParams =
    | {
          eventTrigger: "khanmigo-has-assignment-recommendations";
          dismissKey: string;
      }
    | {
          eventTrigger: "user-entered-answer-to-question";
      }
    | {
          eventTrigger: "video-ended";
          videoTitle: string;
      }
    | {
          eventTrigger: "custom-homepage-greeting";
          greetingType: string;
      };

/**
 * Streamed data containing the audio guide response.
 */
export interface AudioStreamData {
    base64_audio?: string;
    transcript?: string;
}

/**
 * Streamed data containing the text guide response.
 */
export interface TextStreamData {
    text?: string;
    edit?: string;
}

export interface CommandStreamData {
    name: string;
    content: string;
}

/**
 * Streamed data containing error details.
 */
export interface ErrorStreamData {
    debugMessage: string;
}

/**
 * Streamed data containing the guide response metadata.
 */
export interface MetadataStreamData {
    interaction_id: string;
    thread_id: string;
    conversation?: Conversation;
    conversation_entries: ConversationEntry[];
    quota_remaining_percent: string;

    // The Go map is streamed back as an object,
    // so we will do the conversion to a map when
    // we process the streamed data
    feedback_map?: object;

    // Prompt variables that the backend wants the frontend to restore.
    restored_prompt_variables?: Record<string, string>;

    // Output from top-level entry component, if used.
    entry_component_output?: Record<string, any>;
}

/**
 * Streamed data containing the stop-id.
 */
export interface StopIdStreamData {
    interaction_id: string;
}

/**
 * All the possible types of data that can be streamed from the AI guide.
 */
export type StreamData =
    | {
          type: "audio-stream";
          data: AudioStreamData;
      }
    | {
          type: "audio-stream-error";
          data: ErrorStreamData;
      }
    | {
          type: "command-stream";
          data: CommandStreamData;
      }
    | {
          type: "text-stream";
          data: TextStreamData;
      }
    | {
          type: "text-stream-end";
          data: Empty;
      }
    | {
          type: "metadata";
          data: MetadataStreamData;
      }
    | {
          type: "stop-id";
          data: StopIdStreamData;
      }
    | {
          type: "action-status-stream";
          data: ActionStatusStreamData;
      };

// sync-end:khanmigo-client-notifications
export interface AIGuideStreamHandlers {
    /**
     * Called when the answer is updated.
     */
    onAnswer: (answer: string, duration: number) => void;
    /**
     * Called when audio is received.
     * @param base64Audio The base64 encoded audio string.
     * @param transcript The audio transcript.
     */
    onAudio: (data: MessageAudioChunk | {error: KAError}) => void;
    /**
     * Called when the metadata is received.
     * @param metadata The metadata that was received.
     */
    onMetadata: (metadata: AIGuideMetadata) => void;
    /**
     * Called when the stop-id is received.
     * @param stopId The stop-id that was received.
     */
    onStopId: (stopId: InteractionID) => void;
    /**
     * Called when a command-stream is received.
     * @param name The name of the command that was sent.
     * @param content The response to the command.  Could be a plain string, or could be serialized data structure expected
     * to be parsed by the client.  Will vary depending on the command.
     */
    onCommand: (name: string, content: string) => void;
    /**
     * Called when an action-status-stream is received.
     * @param action The action whose status is being updated.
     * @param status The new status of the given action.
     */
    onActionStatus: (
        action: ActionStatusStreamAction,
        status: ActionStatusStreamStatus,
    ) => void;
}
